import { IconSidebarSimpleRightSquareFilled } from "@/assets/icons/central/IconSidebarSimpleRightSquareFilled";
import { IconChevronDownSmall } from "@/assets/icons/geist/IconChevronDownSmall";
import { IconFile } from "@/assets/icons/geist/IconFile";
import { IconPanelRightOpen } from "@/assets/icons/geist/IconPanelRightOpen";
import { IconVSCode } from "@/assets/icons/geist/IconVSCode";
import { IconOnaCopy } from "@/assets/icons/IconOnaCopy";
import { IconOnaDownload } from "@/assets/icons/IconOnaDownload";
import { IconOnaFolder } from "@/assets/icons/IconOnaFolder";
import { IconOnaReset } from "@/assets/icons/IconOnaReset";
import { OnaLettermark } from "@/assets/icons/OnaLettermark";
import type { BaseIconProps } from "@/assets/icons/utils";
import { fromGitpodSize, withGitpodSize } from "@/assets/icons/utils";
import { StatusDot } from "@/components/flexkit/status-dot/StatusDot";
import { getConversationDotProps } from "@/routes/environments/conversation-status";
import { OpenInEditorButton } from "@/components/environments/OpenInEditorButton";
import { SuperOnaOpenPortButton } from "@/components/environments/SuperOnaOpenPortButton";
import { Button } from "@/components/flexkit/Button";
import { NavigationTabs } from "@/components/flexkit/tabs/NavigationTabs";
import { ScrollableTabList } from "@/components/flexkit/tabs/ScrollableTabList";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { cn } from "@/components/podkit/lib/cn";
import { SkeletonText } from "@/components/podkit/loading/Skeleton";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Tooltip } from "@/components/Tooltip";
import { useAgentExecutionActions } from "@/hooks/use-agent-execution-actions";
import { DeleteAgentExecutionModal } from "@/components/ai/DeleteAgentExecutionModal";
import { useCompleteSupportBundle } from "@/hooks/use-complete-support-bundle";
import { useDesktop } from "@/hooks/use-desktop";
import { useTemporaryState } from "@/hooks/use-temporary-value";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import {
    useEnvironment,
    useStartEnvironment,
    useStopEnvironment,
    type PlainEnvironment,
} from "@/queries/environment-queries";
import { getRepoUrl } from "@/repo-url";
import { DeleteEnvironmentModal } from "@/routes/environments/DeleteEnvironmentModal";
import { showStart, showStop } from "@/routes/environments/phase";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { agentExecutionName } from "@/utils/agent-executions";
import { formatError } from "@/utils/errors";
import { ToggleDetailsPanelKeyBinding } from "@/utils/platform";
import { IconFileText } from "central-icons/IconFileText";
import { IconSidebarSimpleRightSquare } from "central-icons/IconSidebarSimpleRightSquare";
import { type ActiveLogView } from "@/atoms/ui-state";
import { useGetService, useGetTask } from "@/queries/automations-queries";

import { Timestamp } from "@bufbuild/protobuf";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { IconPlay } from "@/assets/icons/geist/IconPlay";
import { IconStop } from "@/assets/icons/geist/IconStop";
import { IconTrash } from "@/assets/icons/geist/IconTrash";
import { toTimeoutValue } from "@/components/environments/environment-timeout";
import { AutoStopSubmenu } from "@/components/environments/AutoStopSubmenu";
import { ResourceUsageDialog } from "@/components/environments/ResourceUsageDialog";
import { timeAgo } from "@/format/time";
import { IconCheckmark2Small } from "central-icons/IconCheckmark2Small";
import IconGlobus from "central-icons/IconGlobus";
import { IconHeartBeat } from "central-icons/IconHeartBeat";
import { IconSquareBehindSquare4 } from "central-icons/IconSquareBehindSquare4";

const IconFileTextSized = withGitpodSize(IconFileText);
const IconGlobusSized = withGitpodSize(IconGlobus);
const IconHeartBeatSized = withGitpodSize(IconHeartBeat);
const SidebarIconClosed = withGitpodSize(IconSidebarSimpleRightSquare);

// Stub used when environment data hasn't loaded yet. The hook safely handles
// undefined status (supportBundleUrl will be falsy, so environment.id is never accessed).
const EMPTY_ENVIRONMENT = { id: "", status: undefined } as unknown as PlainEnvironment;

/** Wraps OnaLettermark (raw SVG) to match the BaseIconProps interface used by NavigationTabs.Trigger. */
const OnaLettermarkIcon: FC<BaseIconProps> = ({ size, className, ...props }) => {
    const px = fromGitpodSize(size);
    return <OnaLettermark width={px} height={px} className={className} {...props} />;
};

export type TabName = "code" | "conversation" | "file" | "environment" | "logs" | "browser";

export const SuperOnaConversationHeader: FC<{
    environmentId?: string;
    agentExecution?: PlainAgentExecution | null;
    isDetailsPanelOpen: boolean;
    onToggleDetailsPanel: () => void;
    activeTab?: TabName;
    onTabChange?: (tab: TabName) => void;
    /** File path currently open in the diff tab */
    selectedFilePath?: string | null;
    /** Close the file tab */
    onCloseFileTab?: () => void;
    /** Whether the environment tab is open (persists across tab switches until dismissed) */
    isEnvironmentTabOpen?: boolean;
    /** Dismiss the environment tab */
    onCloseEnvironmentTab?: () => void;
    /** Active automation log view (service or task run) */
    activeLogView?: ActiveLogView | null;
    /** Close the logs tab */
    onCloseLogsTab?: () => void;
    /** All agent executions for this environment, including subagents (for execution tabs) */
    allExecutions?: PlainAgentExecution[];
    /** Switch to a different execution */
    onSelectExecution?: (executionId: string) => void;
    /** Whether the browser VNC port is available */
    hasBrowserPort?: boolean;
    /** Close the browser tab */
    onCloseBrowserTab?: () => void;
}> = ({
    environmentId,
    agentExecution,
    isDetailsPanelOpen,
    onToggleDetailsPanel,
    activeTab: controlledTab,
    onTabChange,
    selectedFilePath,
    onCloseFileTab,
    isEnvironmentTabOpen,
    onCloseEnvironmentTab,
    activeLogView,
    onCloseLogsTab,
    allExecutions,
    onSelectExecution,
    hasBrowserPort,
    onCloseBrowserTab,
}) => {
    const { data: environment } = useEnvironment(environmentId);
    const { isMobileViewport: isMobile } = useIsMobileViewport();
    const { isDesktop } = useDesktop();
    const [internalTab, setInternalTab] = useState<TabName>("conversation");
    const activeTab = controlledTab ?? internalTab;
    const setActiveTab = onTabChange ?? setInternalTab;
    const [showResetModal, setShowResetModal] = useState(false);

    const { copyAgentExecutionId } = useAgentExecutionActions(agentExecution ?? undefined);
    const { downloadSupportBundle, isSupportBundleAvailable, enableTokenFetch } =
        useCompleteSupportBundle(environment ?? EMPTY_ENVIRONMENT, allExecutions);

    // Eagerly enable token fetch — the context menu is always available.
    useEffect(() => {
        enableTokenFetch();
    }, [enableTokenFetch]);

    const conversationName = agentExecution ? agentExecutionName(agentExecution) : "Conversation";
    const hasMultipleExecutions = (allExecutions?.length ?? 0) > 1;

    // Track which execution tab is selected locally so the Radix value
    // updates synchronously on click, before the agentExecution prop
    // catches up from the parent.
    const [selectedExecId, setSelectedExecId] = useState(agentExecution?.id);
    useEffect(() => {
        setSelectedExecId(agentExecution?.id);
    }, [agentExecution?.id]);

    const renderContextMenu = (trackingSuffix: string) =>
        agentExecution ? (
            <>
                <DropdownMenuItem
                    onClick={copyAgentExecutionId}
                    LeadingIcon={IconOnaCopy}
                    data-tracking-id={`copy-session-id${trackingSuffix}`}
                >
                    Copy conversation ID
                </DropdownMenuItem>
                <Tooltip content={!isSupportBundleAvailable ? "Support bundle is temporarily unavailable" : undefined}>
                    <div>
                        <DropdownMenuItem
                            onClick={downloadSupportBundle}
                            disabled={!isSupportBundleAvailable}
                            LeadingIcon={IconOnaDownload}
                            data-tracking-id={`download-support-bundle${trackingSuffix}`}
                        >
                            Download support bundle
                        </DropdownMenuItem>
                    </div>
                </Tooltip>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                    onClick={() => setShowResetModal(true)}
                    variant="destructive"
                    LeadingIcon={IconOnaReset}
                    data-tracking-id={`reset-session${trackingSuffix}`}
                >
                    Reset conversation
                </DropdownMenuItem>
            </>
        ) : undefined;
    const branch = environment?.status?.content?.git?.branch;
    const repoInfo = environment ? getRepoUrl(environment) : undefined;
    const branchDisplayName =
        branch || (repoInfo ? `${repoInfo.account}/${repoInfo.repo}` : undefined) || conversationName;
    const handleOpenEnvironmentDetails = useCallback(() => {
        setActiveTab("environment");
    }, [setActiveTab]);

    const hasDynamicTabs =
        !!selectedFilePath || !!(isEnvironmentTabOpen && environment) || !!activeLogView || !!hasBrowserPort;

    return (
        <div className="flex flex-col">
            {/* Primary header row — environment identity + controls (hidden on mobile; SuperOnaMobileHeader provides this) */}
            <div
                className={cn(
                    "flex h-12 w-full items-center justify-between gap-2 border-b border-border-subtle px-4 py-2",
                    isMobile && "hidden",
                )}
            >
                <div className="flex h-6 min-w-0 flex-1 items-center gap-0.5">
                    <SkeletonText ready={!!environment} className="w-40" size="lg">
                        {environment && (
                            <EnvironmentHeaderDropdown
                                environment={environment}
                                agentExecution={agentExecution ?? undefined}
                                displayName={branchDisplayName}
                                onOpenEnvironmentDetails={handleOpenEnvironmentDetails}
                                allExecutions={allExecutions}
                            />
                        )}
                    </SkeletonText>
                </div>
                <div className="flex items-center justify-end gap-1">
                    {environment && <SuperOnaOpenPortButton environment={environment} />}
                    {environment && <OpenInEditorButton environment={environment} />}
                    {isDesktop ? null : isMobile ? (
                        <Tooltip content="Show environment details">
                            <Button
                                variant="ghost"
                                className="rounded-[4px] border-0 p-0"
                                size="lg"
                                onClick={onToggleDetailsPanel}
                                LeadingIcon={IconPanelRightOpen}
                                aria-label="Show environment details"
                                data-tracking-id="show-environment-details-super-ona-header"
                            />
                        </Tooltip>
                    ) : (
                        <Tooltip
                            usePortal
                            content={
                                <div className="flex flex-row items-center gap-2">
                                    <span>{isDetailsPanelOpen ? "Hide details panel" : "Show details panel"}</span>
                                    <KeyboardCombo keys={ToggleDetailsPanelKeyBinding} />
                                </div>
                            }
                        >
                            <Button
                                variant="ghost"
                                className={cn("group relative rounded-[4px] border-0 p-2")}
                                size="sm"
                                onClick={onToggleDetailsPanel}
                                aria-label={isDetailsPanelOpen ? "Hide right panel" : "Show right panel"}
                                data-tracking-id="toggle-details-panel-super-ona-header"
                            >
                                {isDetailsPanelOpen ? (
                                    <>
                                        <IconSidebarSimpleRightSquareFilled
                                            size="sm"
                                            className={cn(
                                                "absolute transition-opacity duration-300",
                                                "opacity-100 group-hover:opacity-0",
                                            )}
                                        />
                                        <SidebarIconClosed
                                            size="sm"
                                            className={cn(
                                                "transition-opacity duration-300",
                                                "opacity-0 group-hover:opacity-100",
                                            )}
                                        />
                                    </>
                                ) : (
                                    <SidebarIconClosed size="sm" />
                                )}
                            </Button>
                        </Tooltip>
                    )}
                </div>
            </div>

            {/* Tabs sub-header row */}
            <NavigationTabs
                value={
                    hasMultipleExecutions && activeTab === "conversation" && selectedExecId
                        ? `exec:${selectedExecId}`
                        : activeTab
                }
                onValueChange={(v) => {
                    // Intercept execution tab clicks — switch to that execution
                    // instead of changing the content tab.
                    if (v.startsWith("exec:")) {
                        const executionId = v.slice("exec:".length);
                        setSelectedExecId(executionId);
                        onSelectExecution?.(executionId);
                        setActiveTab("conversation");
                        return;
                    }
                    setActiveTab(v as TabName);
                }}
            >
                <ScrollableTabList className="h-12 px-4" borderClassName="border-border-subtle">
                    {/* Static tabs — Code tab hidden on mobile (SuperOnaMobileHeader provides the toggle) */}
                    {!isMobile && (
                        <NavigationTabs.Trigger value="code" LeadingIcon={IconVSCode} className="shrink-0">
                            Code
                        </NavigationTabs.Trigger>
                    )}
                    {hasMultipleExecutions ? (
                        allExecutions!.map((exec) => {
                            const execName = agentExecutionName(exec);
                            return (
                                <NavigationTabs.Trigger
                                    key={exec.id}
                                    value={`exec:${exec.id}`}
                                    LeadingIcon={OnaLettermarkIcon}
                                    contextMenuContent={renderContextMenu("-exec-tab")}
                                >
                                    {execName}
                                </NavigationTabs.Trigger>
                            );
                        })
                    ) : (
                        <NavigationTabs.Trigger
                            value="conversation"
                            LeadingIcon={OnaLettermarkIcon}
                            contextMenuContent={renderContextMenu("-tab")}
                        >
                            {conversationName}
                        </NavigationTabs.Trigger>
                    )}

                    {/* Separator between static and dynamic tabs */}
                    {hasDynamicTabs && <NavigationTabs.Separator />}

                    {/* Dynamic tabs — appear/disappear based on user actions */}
                    {isEnvironmentTabOpen && environment && (
                        <NavigationTabs.Trigger
                            value="environment"
                            LeadingIcon={(props) => {
                                const dotProps = getConversationDotProps(environment, agentExecution ?? undefined);
                                return (
                                    <StatusDot
                                        size="sm"
                                        color={dotProps.color}
                                        variant={dotProps.variant}
                                        animation={dotProps.animation}
                                        className={props.className}
                                    />
                                );
                            }}
                            onClose={() => onCloseEnvironmentTab?.()}
                        >
                            Environment
                        </NavigationTabs.Trigger>
                    )}
                    {activeLogView && (
                        <NavigationTabs.Trigger
                            value="logs"
                            LeadingIcon={IconFileTextSized}
                            onClose={() => onCloseLogsTab?.()}
                        >
                            <LogsTabLabel logView={activeLogView} />
                        </NavigationTabs.Trigger>
                    )}
                    {selectedFilePath && (
                        <NavigationTabs.Trigger value="file" LeadingIcon={IconFile} onClose={() => onCloseFileTab?.()}>
                            {selectedFilePath.split("/").pop() ?? "File"}
                        </NavigationTabs.Trigger>
                    )}
                    {hasBrowserPort && (
                        <NavigationTabs.Trigger
                            value="browser"
                            LeadingIcon={IconGlobusSized}
                            onClose={() => onCloseBrowserTab?.()}
                        >
                            Browser
                        </NavigationTabs.Trigger>
                    )}
                </ScrollableTabList>
            </NavigationTabs>

            {agentExecution && (
                <DeleteAgentExecutionModal
                    agentExecution={agentExecution}
                    open={showResetModal}
                    onClose={() => setShowResetModal(false)}
                />
            )}
        </div>
    );
};

/** Resolves the automation name for the logs tab label from the active log view. */
const LogsTabLabel: FC<{ logView: ActiveLogView }> = ({ logView }) => {
    const isService = logView.type === "service";
    const { data: service } = useGetService(isService ? logView.serviceId : undefined);
    const { data: task } = useGetTask(!isService ? logView.taskId : undefined);
    const name = isService ? service?.metadata?.name : task?.metadata?.name;

    if (name) {
        return <>Logs: {name}</>;
    }
    return <>{isService ? "Service Logs" : "Task Logs"}</>;
};

/** Splits a branch name at the first slash, rendering prefix in secondary color and the rest in primary.
 *  @param bold — when true (default) the rest portion is rendered in bold (used in the trigger). */
export const BranchName: FC<{ name?: string; bold?: boolean; className?: string }> = ({
    name,
    bold = true,
    className,
}) => {
    if (!name) return null;

    const slashIndex = name.indexOf("/");
    if (slashIndex === -1) {
        return (
            <span className={cn("min-w-0 truncate text-left text-content-primary", bold && "font-bold", className)}>
                {name}
            </span>
        );
    }

    const prefix = name.slice(0, slashIndex);
    const rest = name.slice(slashIndex + 1);

    return (
        <span className={cn("flex min-w-0 items-baseline gap-1 truncate text-left", className)}>
            <span className="shrink-0 text-content-secondary">{prefix}</span>
            <span className="shrink-0 text-content-secondary">/</span>
            <span className={cn("min-w-0 truncate text-content-primary", bold && "font-bold")}>{rest}</span>
        </span>
    );
};

export const EnvironmentHeaderDropdown: FC<{
    environment: PlainEnvironment;
    agentExecution?: PlainAgentExecution;
    displayName?: string;
    onOpenEnvironmentDetails?: () => void;
    allExecutions?: PlainAgentExecution[];
}> = ({ environment, agentExecution, displayName, onOpenEnvironmentDetails, allExecutions }) => {
    const dotProps = getConversationDotProps(environment, agentExecution);
    const [isOpen, setIsOpen] = useState(false);
    const [showResourceUsage, setShowResourceUsage] = useState(false);
    const [showDeleteSessionModal, setShowDeleteSessionModal] = useState(false);
    const { toast } = useToast();
    const navigate = useNavigate();
    const startEnvironment = useStartEnvironment();
    const stopEnvironment = useStopEnvironment();
    const { downloadSupportBundle, isSupportBundleAvailable, enableTokenFetch, isTokenReady } =
        useCompleteSupportBundle(environment, allExecutions);

    const canOpenProjectSettings = !!environment.metadata?.projectId;

    const startedTimeAgo = useMemo(() => {
        const createdAt = environment.metadata?.createdAt;
        if (!createdAt) return undefined;
        const date = new Timestamp(createdAt).toDate();
        return `Started ${timeAgo(date)}`;
    }, [environment.metadata?.createdAt]);

    const handleOpenChange = useCallback(
        (open: boolean) => {
            if (open) {
                enableTokenFetch();
            }
            setIsOpen(open);
        },
        [enableTokenFetch],
    );

    const handleOpenProjectSettings = useCallback(() => {
        const projectId = environment.metadata?.projectId;
        if (projectId) {
            navigate(`/projects/${projectId}/settings`);
        }
    }, [environment.metadata?.projectId, navigate]);

    const branch = environment.status?.content?.git?.branch;
    const [branchCopied, setBranchCopied] = useTemporaryState(false, 2000);

    const handleCopyBranch = useCallback(async () => {
        if (!branch) return;
        try {
            await navigator.clipboard.writeText(branch);
            setBranchCopied(true);
            toast({
                title: "Branch name copied to clipboard",
                description: branch,
            });
        } catch {
            // clipboard may be unavailable
        }
    }, [branch, setBranchCopied, toast]);

    const handleCopyId = useCallback(async () => {
        await navigator.clipboard.writeText(environment.id);
        toast({
            title: "Environment ID copied to clipboard",
            description: environment.id,
        });
    }, [toast, environment.id]);

    const handleStart = useCallback(async () => {
        try {
            await startEnvironment.mutateAsync({ environmentId: environment.id });
        } catch (error) {
            toast({
                title: "Failed to start environment",
                description: formatError(error),
            });
        }
    }, [startEnvironment, environment.id, toast]);

    const handleStop = useCallback(() => {
        stopEnvironment.mutate(environment.id, {
            onError: (e) => {
                toast({
                    title: "Failed to stop environment",
                    description: formatError(e),
                });
            },
        });
    }, [stopEnvironment, toast, environment.id]);

    return (
        <>
            <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
                <DropdownMenuTrigger
                    data-testid="environment-header-dropdown-trigger"
                    className="group flex min-w-0 cursor-pointer items-center focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-0 focus-visible:outline-content-primary"
                    aria-label="Environment actions"
                >
                    <div
                        className={cn(
                            "flex min-w-0 items-center gap-1 self-stretch rounded-l-md p-1 transition-colors group-hover:bg-surface-hover group-data-[state=open]:bg-surface-hover",
                        )}
                    >
                        <StatusDot
                            size="sm"
                            color={dotProps.color}
                            variant={dotProps.variant}
                            animation={dotProps.animation}
                            tooltip={dotProps.tooltip}
                            className="shrink-0"
                        />
                        <BranchName name={displayName} className="text-base" />
                    </div>
                    <div
                        className={cn(
                            "flex flex-shrink-0 items-center self-stretch rounded-r-md px-1 transition-colors group-hover:bg-surface-hover group-data-[state=open]:bg-surface-hover",
                        )}
                    >
                        <IconChevronDownSmall size="sm" className="size-4" />
                    </div>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="start" className="w-64">
                    {/* Header section: status dot + branch name + copy + started time */}
                    <DropdownMenuLabel className="flex items-center gap-1.5 px-3 py-2">
                        <StatusDot
                            size="sm"
                            color={dotProps.color}
                            variant={dotProps.variant}
                            animation={dotProps.animation}
                            className="shrink-0 self-start mt-1"
                        />
                        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                            <BranchName name={displayName} bold={false} className="text-base font-normal" />
                            {startedTimeAgo && (
                                <span className="text-sm font-normal text-content-secondary">{startedTimeAgo}</span>
                            )}
                        </div>
                        {branch && (
                            <button
                                type="button"
                                className="shrink-0 self-center rounded p-1 text-content-strong hover:text-content-primary"
                                onClick={handleCopyBranch}
                                aria-label="Copy branch name"
                                data-tracking-id="copy-branch-name-super-ona-header"
                            >
                                <span className="relative inline-flex size-4 items-center justify-center">
                                    <IconSquareBehindSquare4
                                        size={16}
                                        className={cn(
                                            "absolute inset-0 transition-all duration-150 ease-in-out motion-reduce:transition-none",
                                            branchCopied
                                                ? "scale-90 opacity-0"
                                                : "scale-100 opacity-100",
                                        )}
                                    />
                                    <IconCheckmark2Small
                                        size={16}
                                        className={cn(
                                            "absolute inset-0 text-content-success transition-all duration-150 ease-in-out motion-reduce:transition-none",
                                            branchCopied
                                                ? "scale-100 opacity-100"
                                                : "scale-90 opacity-0",
                                        )}
                                    />
                                </span>
                            </button>
                        )}
                    </DropdownMenuLabel>

                    <DropdownMenuSeparator />

                    {/* Group 1: Environment actions */}
                    {onOpenEnvironmentDetails && (
                        <DropdownMenuItem
                            onClick={onOpenEnvironmentDetails}
                            LeadingIcon={IconFileTextSized}
                            data-testid="menu-item-environment-details"
                            data-tracking-id="environment-details-super-ona-header"
                        >
                            Environment details
                        </DropdownMenuItem>
                    )}

                    <DropdownMenuItem
                        onClick={handleCopyId}
                        LeadingIcon={IconOnaCopy}
                        data-tracking-id="copy-environment-id-super-ona-header"
                    >
                        Copy environment ID
                    </DropdownMenuItem>

                    <DropdownMenuItem
                        onClick={() => setShowResourceUsage(true)}
                        LeadingIcon={IconHeartBeatSized}
                        data-testid="menu-item-resource-usage"
                        data-tracking-id="resource-usage-super-ona-header"
                    >
                        Resource usage
                    </DropdownMenuItem>

                    <AutoStopSubmenu
                        environmentId={environment.id}
                        timeoutSeconds={toTimeoutValue(environment.spec?.timeout?.disconnected)}
                        trackingIdSuffix="super-ona-header"
                    />

                    {showStart(environment) && (
                        <DropdownMenuItem
                            onClick={handleStart}
                            LeadingIcon={(props) => (
                                <IconPlay {...props} className={cn(props.className, "text-content-secondary")} />
                            )}
                            data-tracking-id="start-environment-super-ona-header"
                        >
                            Start environment
                        </DropdownMenuItem>
                    )}
                    {showStop(environment) && (
                        <DropdownMenuItem
                            onClick={handleStop}
                            LeadingIcon={(props) => (
                                <IconStop {...props} className={cn(props.className, "text-content-secondary")} />
                            )}
                            data-tracking-id="stop-environment-super-ona-header"
                        >
                            Stop environment
                        </DropdownMenuItem>
                    )}

                    <DropdownMenuSeparator />

                    {/* Group 2: Project & support */}
                    <Tooltip
                        content={!canOpenProjectSettings ? "No project associated with this environment" : undefined}
                    >
                        <div>
                            <DropdownMenuItem
                                onClick={handleOpenProjectSettings}
                                disabled={!canOpenProjectSettings}
                                LeadingIcon={IconOnaFolder}
                                data-tracking-id="go-to-project-super-ona-header"
                            >
                                Go to project
                            </DropdownMenuItem>
                        </div>
                    </Tooltip>

                    {isSupportBundleAvailable && (
                        <DropdownMenuItem
                            onClick={downloadSupportBundle}
                            disabled={!isTokenReady}
                            LeadingIcon={IconOnaDownload}
                            data-tracking-id="download-support-bundle-super-ona-header"
                        >
                            Download support bundle
                        </DropdownMenuItem>
                    )}

                    {/* Group 3: Destructive actions */}
                    {agentExecution && (
                        <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                variant="destructive"
                                onClick={() => setShowDeleteSessionModal(true)}
                                LeadingIcon={IconTrash}
                                data-tracking-id="delete-session-super-ona-header"
                            >
                                Delete environment
                            </DropdownMenuItem>
                        </>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>

            <ResourceUsageDialog
                environmentId={environment.id}
                open={showResourceUsage}
                onOpenChange={setShowResourceUsage}
            />

            <DeleteEnvironmentModal
                environment={environment}
                agentExecution={agentExecution}
                open={showDeleteSessionModal}
                onClose={() => setShowDeleteSessionModal(false)}
            />
        </>
    );
};
